import { useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, Text } from '@mantine/core';
import { useTree } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { KpiCard } from '../../components/ui/KpiCard';
import { useMasterData } from './useMasterData';
import { buildMasterDataTally, type MasterDataTally } from './masterDataTally';
import { MasterDataTree, companyNodeValue, decodeMasterDataNode, plantNodeValue } from './MasterDataTree';
import { CompanyDetail } from './CompanyDetail';
import { CreateCompanyModal } from './CreateCompanyModal';
import { PlantDetail } from './PlantDetail';
import { useDirtyGuard } from './useDirtyGuard';
import classes from './MasterDataPage.module.css';

/** Bestandszahlen als KpiCard-Reihe -- dieselben Karten wie auf dem Dashboard, statt der
 * frueheren Zeile aus Zahl/Label-Paaren. */
function TallyCards({ tally }: { tally: MasterDataTally }) {
  const items: Array<{ label: string; value: number }> = [
    { label: 'Unternehmen', value: tally.companies },
    { label: 'Werke', value: tally.plants },
    { label: 'Drehöfen', value: tally.kilns },
    { label: 'Werke ohne Ofen', value: tally.plantsWithoutKiln },
  ];

  return (
    <Grid gap="md" mt="md" mb="lg">
      {items.map((item) => (
        <Grid.Col key={item.label} span={{ base: 12, sm: 6, md: 3 }}>
          <KpiCard label={item.label} value={item.value} />
        </Grid.Col>
      ))}
    </Grid>
  );
}

export function MasterDataPage() {
  const { companies, plants, kilns, loading, error, reload } = useMasterData();
  const tree = useTree();
  const dirtyGuard = useDirtyGuard();
  const isWide = useMediaQuery('(min-width: 48em)', true);
  const [showDetailOnMobile, setShowDetailOnMobile] = useState(false);
  const [createCompanyOpen, setCreateCompanyOpen] = useState(false);
  const [focusPlantHeadingToken, setFocusPlantHeadingToken] = useState(0);

  const tally = useMemo(() => buildMasterDataTally(companies, plants, kilns), [companies, plants, kilns]);
  const selection = decodeMasterDataNode(tree.selectedState[0]);

  function selectCompany(companyId: number) {
    const value = companyNodeValue(companyId);
    tree.select(value);
    tree.expand(value);
    setShowDetailOnMobile(true);
  }

  function selectPlant(plantId: number, companyId: number) {
    tree.select(plantNodeValue(plantId));
    tree.expand(companyNodeValue(companyId));
    setShowDetailOnMobile(true);
  }

  /** Sprung aus der verlinkten Werkszeile im Unternehmensdetail (D-04, UI-SPEC Punkt 8) bzw. nach
   * "Werk hinzufügen": waehlt aus wie `selectPlant`, bewegt den Fokus zusaetzlich auf die neue
   * Werks-Ueberschrift. */
  function jumpToPlant(plantId: number, companyId: number) {
    selectPlant(plantId, companyId);
    setFocusPlantHeadingToken((token) => token + 1);
  }

  // Ebenenwechsel-Schutz (D-02, UI-SPEC Punkt 5): jeder vom Nutzer ausgeloeste Auswahlwechsel
  // laeuft durch requestChange -- Baumklick/Enter im Baum UND Sprung aus der verlinkten
  // Werkszeile im Unternehmensdetail. Programmatische Selects (nach "Unternehmen anlegen"/
  // "Werk hinzufügen"/Loeschen) bleiben unguarded, dort gibt es nichts stillschweigend zu
  // verwerfen -- die vorherige Ebene ist bereits abgeschlossen bzw. verschwunden.
  function guardedSelectCompany(companyId: number) {
    dirtyGuard.requestChange(() => selectCompany(companyId));
  }

  function guardedSelectPlant(plantId: number, companyId: number) {
    dirtyGuard.requestChange(() => selectPlant(plantId, companyId));
  }

  function guardedJumpToPlant(plantId: number, companyId: number) {
    dirtyGuard.requestChange(() => jumpToPlant(plantId, companyId));
  }

  async function handleCompanyCreated(companyId: number) {
    await reload();
    selectCompany(companyId);
  }

  function renderDetail() {
    if (selection?.type === 'company') {
      const company = companies.find((c) => c.id === selection.id);
      if (company) {
        return (
          <CompanyDetail
            key={`company-${company.id}`}
            company={company}
            plants={plants.filter((p) => p.company?.id === company.id)}
            reload={reload}
            onJumpToPlant={guardedJumpToPlant}
            onDeleted={() => tree.clearSelected()}
            dirtyGuard={dirtyGuard}
          />
        );
      }
    }
    if (selection?.type === 'plant') {
      const plant = plants.find((p) => p.id === selection.id);
      // Volle Company (inkl. csiMember) aus der Top-Level-Collection nachschlagen -- die an
      // Plant eingebettete Company traegt nur Felder mit 'plant:read' (id/name), csiMember hat
      // ausschliesslich 'company:read'/'company:write' (Plant.php:55 vs. Company.php:55).
      const plantCompany = plant?.company ? companies.find((c) => c.id === plant.company!.id) : undefined;
      if (plant && plantCompany) {
        return (
          <PlantDetail
            key={`plant-${plant.id}`}
            plant={plant}
            company={plantCompany}
            reload={reload}
            onDeleted={(companyId) => selectCompany(companyId)}
            focusToken={focusPlantHeadingToken}
            dirtyGuard={dirtyGuard}
          />
        );
      }
    }
    return (
      <Box className={classes.centered}>
        <Text data-testid="master-data-detail-empty" size="sm" c="vdzGray.6">
          Wählen Sie links ein Unternehmen oder Werk aus.
        </Text>
      </Box>
    );
  }

  const navVisible = isWide || !showDetailOnMobile;
  const detailVisible = isWide || showDetailOnMobile;

  return (
    <Box>
      <PageHeader
        title="Stammdaten"
        breadcrumb={{ label: 'Administration' }}
        rightSection={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateCompanyOpen(true)}>
            Unternehmen anlegen
          </Button>
        }
      />

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md">
          {error}
        </Alert>
      )}
      <TallyCards tally={tally} />

      <Box className={isWide ? classes.split : classes.splitStacked}>
        {navVisible && (
          <Box className={classes.nav}>
            {loading ? (
              <Box className={classes.centered} data-testid="master-data-nav-loading">
                <Text size="sm" c="vdzGray.6">
                  Lädt…
                </Text>
              </Box>
            ) : (
              <MasterDataTree
                companies={companies}
                plants={plants}
                tree={tree}
                onSelectCompany={guardedSelectCompany}
                onSelectPlant={guardedSelectPlant}
                onCreateCompany={() => setCreateCompanyOpen(true)}
              />
            )}
          </Box>
        )}

        {detailVisible && (
          <Box className={classes.detail}>
            {!isWide && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => setShowDetailOnMobile(false)}
                mb="md"
              >
                Zurück zur Übersicht
              </Button>
            )}
            {!loading && renderDetail()}
          </Box>
        )}
      </Box>

      <CreateCompanyModal
        opened={createCompanyOpen}
        onClose={() => setCreateCompanyOpen(false)}
        onCreated={handleCompanyCreated}
      />
    </Box>
  );
}
